/**
 * useSimulatorReport — fetches a per-session report.
 *
 * The edge function generates the report synchronously inside `/report/get`,
 * but generation can take a few seconds (LLM call). We retry on:
 *   - HTTP 409 `session_still_active` (terminal state hasn't propagated yet
 *     after a fresh `/end`)
 *   - HTTP 502 transient provider errors
 *
 * Polling is capped at ~30s total to avoid runaway loops.
 *
 * Spec: docs/architecture/simulador-spec.md §2.5 / §5.5.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { getReport } from "../api/client";
import type { TranscriptMessage } from "../api/client";
import type { ReportShape } from "../types";

interface UseSimulatorReportArgs {
  sessionId: string | null;
  enabled?: boolean;
}

interface UseSimulatorReportResult {
  report: ReportShape | null;
  transcript: TranscriptMessage[] | null;
  isLoading: boolean;
  error: string | null;
  /** True when polling exceeded MAX_TOTAL_MS without a report. */
  timedOut: boolean;
  refetch: () => Promise<void>;
}

const POLL_MS = 2000;
const MAX_TOTAL_MS = 60_000;

export function useSimulatorReport(
  { sessionId, enabled = true }: UseSimulatorReportArgs,
): UseSimulatorReportResult {
  const { session } = useSession();
  const [report, setReport] = useState<ReportShape | null>(null);
  const [transcript, setTranscript] = useState<TranscriptMessage[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState<boolean>(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef<boolean>(false);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const fetchOnce = useCallback(
    async (startedAt: number): Promise<void> => {
      if (!sessionId || cancelledRef.current) return;
      try {
        const token = await session?.getToken();
        if (!token) {
          setError("Sessão expirou. Faça login de novo.");
          setIsLoading(false);
          return;
        }
        const res = await getReport(sessionId, token);
        if (cancelledRef.current) return;
        // Report may briefly be null right after /end before async generation
        // completes. Treat null/empty as transient and keep polling.
        if (!res.report) {
          const elapsed = Date.now() - startedAt;
          if (elapsed < MAX_TOTAL_MS && !cancelledRef.current) {
            timerRef.current = setTimeout(() => void fetchOnce(startedAt), POLL_MS);
            return;
          }
          setTimedOut(true);
          setIsLoading(false);
          return;
        }
        setReport(res.report);
        setTranscript(res.transcript ?? null);
        setError(null);
        setTimedOut(false);
        setIsLoading(false);
      } catch (err) {
        const e = err as Error & { status?: number; code?: string };
        const elapsed = Date.now() - startedAt;
        const transient =
          e.status === 409 ||
          e.code === "session_still_active" ||
          e.status === 502 ||
          e.status === 504;
        if (transient && elapsed < MAX_TOTAL_MS && !cancelledRef.current) {
          timerRef.current = setTimeout(() => void fetchOnce(startedAt), POLL_MS);
          return;
        }
        if (cancelledRef.current) return;
        if (transient) {
          // exhausted retry budget on transient error — surface timeout UX
          setTimedOut(true);
          setIsLoading(false);
          return;
        }
        setError(e.message ?? "Erro ao carregar relatório.");
        setIsLoading(false);
      }
    },
    [sessionId, session],
  );

  const refetch = useCallback(async (): Promise<void> => {
    if (!sessionId) return;
    stopTimer();
    cancelledRef.current = false;
    setIsLoading(true);
    setError(null);
    setTimedOut(false);
    await fetchOnce(Date.now());
  }, [fetchOnce, sessionId, stopTimer]);

  useEffect(() => {
    cancelledRef.current = false;
    if (!enabled || !sessionId) {
      setReport(null);
      setTranscript(null);
      setIsLoading(false);
      setError(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    setTimedOut(false);
    void fetchOnce(Date.now());
    return () => {
      cancelledRef.current = true;
      stopTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, enabled]);

  return { report, transcript, isLoading, error, timedOut, refetch };
}
